import * as React from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import { useTheme } from "@mui/material/styles";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";

export default function FAQ() {
  const [expanded, setExpanded] = React.useState(false);
  const theme = useTheme();

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };
  const { t } = useTranslation();

  // Funktion för att konvertera text med WhatsApp-nummer och e-postadresser till klickbara länkar
  const createLinkedText = (text) => {
    const whatsappNumber = "46700400450"; // Svenskt nummer i internationellt format
    const email = "info@partihandel.se";
    const defaultMessage = "Hej! Jag är intresserad av en kostnadsfri demo.";
    
    // Dela upp texten i delar
    const parts = [];
    let lastIndex = 0;
    
    // RegEx för att hitta WhatsApp-nummer (0700400450 eller 0700 400 450)
    const whatsappRegex = /0700\s?400\s?450/g;
    // RegEx för att hitta e-postadresser
    const emailRegex = /info@partihandel\.se/g;
    
    let match;
    const matches = [];
    
    // Hitta alla WhatsApp-nummer
    while ((match = whatsappRegex.exec(text)) !== null) {
      matches.push({
        type: 'whatsapp',
        start: match.index,
        end: match.index + match[0].length,
        text: match[0]
      });
    }
    
    // Hitta alla e-postadresser
    while ((match = emailRegex.exec(text)) !== null) {
      matches.push({
        type: 'email',
        start: match.index,
        end: match.index + match[0].length,
        text: match[0]
      });
    }
    
    // Sortera matches efter position
    matches.sort((a, b) => a.start - b.start);
    
    // Bygg upp JSX med länkar
    matches.forEach((match, index) => {
      // Lägg till text före matchen
      if (match.start > lastIndex) {
        parts.push(text.substring(lastIndex, match.start));
      }
      
      // Lägg till länken
      if (match.type === 'whatsapp') {
        // Använd officiella wa.me formatet enligt WhatsApp dokumentation
        const encodedMessage = encodeURIComponent(defaultMessage);
        const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
        
        parts.push(
          <Link
            key={`whatsapp-${index}`}
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              fontWeight: 600,
              '&:hover': {
                textDecoration: 'underline',
              }
            }}
          >
            {match.text}
          </Link>
        );
      } else if (match.type === 'email') {
        // Förbättrad mailto med korrekt encoding
        const subject = encodeURIComponent('Demo - Partihandel');
        const body = encodeURIComponent(defaultMessage);
        const mailtoUrl = `mailto:${email}?subject=${subject}&body=${body}`;
        
        parts.push(
          <Link
            key={`email-${index}`}
            href={mailtoUrl}
            onClick={(e) => {
              // Fallback för miljöer där mailto inte fungerar
              if (!window.confirm('Öppna din e-postklient för att skicka meddelande till ' + email + '?')) {
                e.preventDefault();
                // Kopiera e-postadressen till clipboard som fallback
                navigator.clipboard.writeText(email).catch(() => {});
              }
            }}
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              fontWeight: 600,
              '&:hover': {
                textDecoration: 'underline',
              }
            }}
          >
            {match.text}
          </Link>
        );
      }
      
      lastIndex = match.end;
    });
    
    // Lägg till återstående text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 1 ? parts : text;
  };

  return (
    <Box
      id="faq"
      sx={{
        pt: { xs: 4, sm: 12 },
        pb: { xs: 10, sm: 14 },
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
          : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      }}
    >
      <Container
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: { xs: 3, sm: 6 },
        }}
      >
        <Typography
          component="h2"
          variant="h4"
          sx={{
            width: { sm: "100%", md: "60%" },
            textAlign: { sm: "left", md: "center" },
            color: 'text.primary',
            fontWeight: 600,
          }}
        >
          {t("landingpage.topbar.faq")}
        </Typography>
        <Box sx={{ width: "100%" }}>
          <Accordion
            expanded={expanded === "panel1"}
            onChange={handleChange("panel1")}
            sx={{
              background: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.1)' 
                : 'rgba(0, 0, 0, 0.1)'}`,
              mb: 1,
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1d-content"
              id="panel1d-header"
            >
              <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t("landingpage.faq1")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="body2"
                gutterBottom
                sx={{ 
                  maxWidth: { sm: "100%", md: "70%" },
                  color: 'text.secondary',
                  lineHeight: 1.6
                }}
              >
                {createLinkedText(t("landingpage.faq1text"))}
              </Typography>
            </AccordionDetails>
          </Accordion>
          
          <Accordion
            expanded={expanded === "panel2"}
            onChange={handleChange("panel2")}
            sx={{
              background: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.1)' 
                : 'rgba(0, 0, 0, 0.1)'}`,
              mb: 1,
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel2d-content"
              id="panel2d-header"
            >
              <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t("landingpage.faq2")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="body2"
                gutterBottom
                sx={{ 
                  maxWidth: { sm: "100%", md: "70%" },
                  color: 'text.secondary',
                  lineHeight: 1.6
                }}
              >
                {createLinkedText(t("landingpage.faq2text"))}
              </Typography>
            </AccordionDetails>
          </Accordion>
          
          <Accordion
            expanded={expanded === "panel3"}
            onChange={handleChange("panel3")}
            sx={{
              background: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.1)' 
                : 'rgba(0, 0, 0, 0.1)'}`,
              mb: 1,
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel3d-content"
              id="panel3d-header"
            >
              <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t("landingpage.faq3")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="body2"
                gutterBottom
                sx={{ 
                  maxWidth: { sm: "100%", md: "70%" },
                  color: 'text.secondary',
                  lineHeight: 1.6
                }}
              >
                {createLinkedText(t("landingpage.faq3text"))}
              </Typography>
            </AccordionDetails>
          </Accordion>
          
          <Accordion
            expanded={expanded === "panel4"}
            onChange={handleChange("panel4")}
            sx={{
              background: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.1)' 
                : 'rgba(0, 0, 0, 0.1)'}`,
              mb: 1,
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel4d-content"
              id="panel4d-header"
            >
              <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t("landingpage.faq4")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="body2"
                gutterBottom
                sx={{ 
                  maxWidth: { sm: "100%", md: "70%" },
                  color: 'text.secondary',
                  lineHeight: 1.6
                }}
              >
                {createLinkedText(t("landingpage.faq4text"))}
              </Typography>
            </AccordionDetails>
          </Accordion>
          
          <Accordion
            expanded={expanded === "panel5"}
            onChange={handleChange("panel5")}
            sx={{
              background: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.1)' 
                : 'rgba(0, 0, 0, 0.1)'}`,
              mb: 1,
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel5d-content"
              id="panel5d-header"
            >
              <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t("landingpage.faq5")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="body2"
                gutterBottom
                sx={{ 
                  maxWidth: { sm: "100%", md: "70%" },
                  color: 'text.secondary',
                  lineHeight: 1.6
                }}
              >
                {createLinkedText(t("landingpage.faq5text"))}
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded === "panel6"}
            onChange={handleChange("panel6")}
            sx={{
              background: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.1)' 
                : 'rgba(0, 0, 0, 0.1)'}`,
              mb: 1,
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel6d-content"
              id="panel6d-header"
            >
              <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t("landingpage.faq6")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="body2"
                gutterBottom
                sx={{ 
                  maxWidth: { sm: "100%", md: "70%" },
                  color: 'text.secondary',
                  lineHeight: 1.6
                }}
              >
                {createLinkedText(t("landingpage.faq6text"))}
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded === "panel7"}
            onChange={handleChange("panel7")}
            sx={{
              background: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.1)' 
                : 'rgba(0, 0, 0, 0.1)'}`,
              mb: 1,
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel7d-content"
              id="panel7d-header"
            >
              <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t("landingpage.faq7")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="body2"
                gutterBottom
                sx={{ 
                  maxWidth: { sm: "100%", md: "70%" },
                  color: 'text.secondary',
                  lineHeight: 1.6
                }}
              >
                {createLinkedText(t("landingpage.faq7text"))}
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded === "panel8"}
            onChange={handleChange("panel8")}
            sx={{
              background: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.1)' 
                : 'rgba(0, 0, 0, 0.1)'}`,
              mb: 1,
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel8d-content"
              id="panel8d-header"
            >
              <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t("landingpage.faq8")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="body2"
                gutterBottom
                sx={{ 
                  maxWidth: { sm: "100%", md: "70%" },
                  color: 'text.secondary',
                  lineHeight: 1.6
                }}
              >
                {createLinkedText(t("landingpage.faq8text"))}
              </Typography>
            </AccordionDetails>
          </Accordion>
          
          <Accordion
            expanded={expanded === "panel9"}
            onChange={handleChange("panel9")}
            sx={{
              background: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.1)' 
                : 'rgba(0, 0, 0, 0.1)'}`,
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel9d-content"
              id="panel9d-header"
            >
              <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t("landingpage.faq9")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="body2"
                gutterBottom
                sx={{ 
                  maxWidth: { sm: "100%", md: "70%" },
                  color: 'text.secondary',
                  lineHeight: 1.6
                }}
              >
                {createLinkedText(t("landingpage.faq9text"))}
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Container>
    </Box>
  );
}